/** Saves a Blob through a temporary object URL. */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before revoking.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface SheetColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
  width?: number;
  /** Excel number format, e.g. '#,##0 €' or '0.0%'. */
  numFmt?: string;
}

export interface SheetSpec<T> {
  name: string;
  columns: SheetColumn<T>[];
  rows: T[];
}

/** Builds an .xlsx workbook in memory. exceljs is loaded on demand so it never weighs on first paint. */
export async function buildXlsx<T>(sheets: SheetSpec<T>[]): Promise<ArrayBuffer> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  for (const sheet of sheets) {
    const ws = workbook.addWorksheet(sheet.name.slice(0, 31) || 'Data');
    ws.columns = sheet.columns.map((c) => ({
      header: c.header,
      width: c.width ?? Math.max(12, c.header.length + 2),
      style: c.numFmt ? { numFmt: c.numFmt } : undefined,
    }));
    ws.getRow(1).font = { bold: true };
    for (const row of sheet.rows) ws.addRow(sheet.columns.map((c) => c.value(row) ?? ''));
  }
  return (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
}

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Writes rows to an .xlsx file and downloads it. */
export async function exportToXlsx<T>(fileName: string, sheets: SheetSpec<T>[]) {
  const buffer = await buildXlsx(sheets);
  downloadBlob(
    new Blob([buffer], { type: XLSX_MIME }),
    fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`,
  );
}
